import React, { useState } from "react";
import { MapPin, Clock, Pencil, Loader2, Check, X } from "lucide-react";
import { updateTeamProfile } from "../../../api";
import { useToast } from "../../../hooks/use-toast";
import { SERVICES, memberServices } from "../../../lib/services";

const inputCls = "border border-neutral-300 rounded-md px-2 py-1 text-xs focus:outline-none focus:border-brand";

/** Profil easer : adresse de résidence (géocodée), rayon d'intervention (km) et heures hebdo — modifiables par le super-admin. */
const ProfileEditor = ({ u, defaultRadius = 40, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ adresse: u.adresse || "", radius_km: u.radius_km || "", weekly_hours: u.weekly_hours || "", services: u.services_manual ? memberServices(u) : [] });
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();
  const home = u.home_gps;
  const toggleService = (s) => setF({ ...f, services: f.services.includes(s) ? f.services.filter((x) => x !== s) : [...f.services, s] });

  const save = async () => {
    setSaving(true);
    try {
      const r = await updateTeamProfile(u.id, {
        adresse: f.adresse, radius_km: f.radius_km === "" ? null : parseFloat(f.radius_km), weekly_hours: f.weekly_hours === "" ? null : parseFloat(f.weekly_hours),
        services: f.services,
      });
      toast({ title: "Profil mis à jour ✓", description: r.data.home_gps ? "Domicile localisé." : f.adresse ? "⚠️ Adresse non localisée." : undefined });
      setOpen(false); onSaved(r.data);
    } catch (e) { toast({ title: "Erreur", description: e?.response?.data?.detail || "Échec", variant: "destructive" }); }
    finally { setSaving(false); }
  };

  return (
    <div className="w-full mt-2 text-xs" data-testid={`profile-${u.id}`}>
      {!open ? (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-neutral-500">
          <span className={`flex items-center gap-1 ${home ? "" : "text-amber-600"}`}><MapPin size={12} /> {u.adresse ? u.adresse : "domicile non renseigné"}{u.adresse && !home ? " (non localisé)" : ""}</span>
          <span>Rayon : <b className="text-neutral-700" data-testid={`profile-radius-${u.id}`}>{u.radius_km || defaultRadius} km</b>{!u.radius_km ? " (défaut)" : ""}</span>
          <span className="flex items-center gap-1"><Clock size={12} /> {u.weekly_hours ? `${u.weekly_hours} h/sem (contrat)` : "heures hebdo non renseignées"}</span>
          <button onClick={() => setOpen(true)} className="text-brand hover:underline flex items-center gap-1" data-testid={`profile-edit-${u.id}`}><Pencil size={11} /> Modifier</button>
        </div>
      ) : (
        <div className="flex flex-wrap items-end gap-2 bg-neutral-50 border border-neutral-200 rounded-lg p-2">
          <label className="flex-1 min-w-[220px]"><span className="block text-[10px] font-semibold text-neutral-500">Adresse de résidence</span>
            <input className={`${inputCls} w-full`} value={f.adresse} onChange={(e) => setF({ ...f, adresse: e.target.value })} placeholder="ex : 4 rue de la Ligue 80160 Conty" data-testid={`profile-adresse-${u.id}`} /></label>
          <label><span className="block text-[10px] font-semibold text-neutral-500">Rayon (km)</span>
            <input type="number" min={1} max={500} className={`${inputCls} w-20`} value={f.radius_km} onChange={(e) => setF({ ...f, radius_km: e.target.value })} placeholder={String(defaultRadius)} data-testid={`profile-radius-input-${u.id}`} /></label>
          <label><span className="block text-[10px] font-semibold text-neutral-500">Heures / sem</span>
            <input type="number" min={0} max={60} step={0.5} className={`${inputCls} w-20`} value={f.weekly_hours} onChange={(e) => setF({ ...f, weekly_hours: e.target.value })} data-testid={`profile-hours-input-${u.id}`} /></label>
          <div><span className="block text-[10px] font-semibold text-neutral-500" title="Vide = suivre les tags Dolibarr">Service (forcer)</span>
            <div className="flex gap-2 py-1">
              {Object.entries(SERVICES).map(([k, s]) => (
                <label key={k} className="flex items-center gap-1 text-xs text-neutral-700"><input type="checkbox" className="accent-brand" checked={f.services.includes(k)} onChange={() => toggleService(k)} data-testid={`profile-service-${u.id}-${k}`} /> {s.label}</label>
              ))}
            </div>
          </div>
          <button onClick={save} disabled={saving} className="bg-brand text-white rounded-md px-2.5 py-1.5 flex items-center gap-1 font-semibold" data-testid={`profile-save-${u.id}`}>{saving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Enregistrer</button>
          <button onClick={() => setOpen(false)} className="text-neutral-500 p-1.5"><X size={13} /></button>
        </div>
      )}
    </div>
  );
};

export default ProfileEditor;
